import React, { type ReactNode, useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AddIcon from '@atlaskit/icon/core/add';
import { CloseButton } from '@atlaskit/modal-dialog';
import { Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	AgentCreateButton,
	type AgentCreateButtonProps,
} from '../../../controllers/agent-create-button/index.tsx';
import { useAgentSitePermissions } from '../../../controllers/agent-site-permissions/index.tsx';
import { useBrowseAgentsUIStore } from '../../../controllers/browse-agents-ui/index.tsx';

import messages from './messages.ts';

const styles = cssMap({
	browseAgentsHeaderWrapper: {
		width: '100%',
		alignItems: 'start',
	},

	browseAgentsHeader: {
		flexWrap: 'wrap',
		width: '100%',
		justifyContent: 'space-between',
		'@media (min-width: 48rem)': {
			flexWrap: 'nowrap',
		},
	},
	titleSection: {
		alignItems: 'start',
		gap: token('space.100'),
	},
	buttonsSection: {
		gap: token('space.100'),
		flexDirection: 'row-reverse',
	},
});

type Props = {
	titleElement: ReactNode;
	subtitleElement: ReactNode;
	onClose?: (analyticsEvent: UIAnalyticsEvent) => void;
	siteId: string;
	/**
	 * Optional override for the "Create agent" button's click behaviour.
	 * Forwarded to {@link AgentCreateButton}; see its prop docs for details.
	 */
	onClickCreateAgent?: AgentCreateButtonProps['onClickCreateAgent'];
};

export const BrowseAgentsHeader = ({
	titleElement,
	subtitleElement,
	onClose,
	siteId,
	onClickCreateAgent,
}: Props): React.JSX.Element => {
	di(useAgentSitePermissions);

	const { formatMessage } = useIntl();

	const [_, { resetBrowseAgentsUI }] = useBrowseAgentsUIStore();

	const handleClose = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			if (onClose) {
				onClose(analyticsEvent);
				resetBrowseAgentsUI();
			}
		},
		[onClose, resetBrowseAgentsUI],
	);

	const isRecommendationsM1 = FeatureGates.getExperimentValue(
		'jira_agent_recommendations_m1',
		'isEnabled',
		false,
	);

	return (
		<Flex xcss={styles.browseAgentsHeaderWrapper} testId="browse-agents-header-refresh">
			<Inline space="space.200" alignBlock="start" xcss={styles.browseAgentsHeader}>
				<Stack alignBlock="start" xcss={styles.titleSection}>
					{titleElement}
					{subtitleElement}
				</Stack>
				<Flex xcss={styles.buttonsSection}>
					{onClose && (
						<CloseButton
							onClick={(_, analyticsEvent) => handleClose(analyticsEvent)}
							label={formatMessage(messages.closeButton)}
						/>
					)}
					{!isRecommendationsM1 && (
						<AgentCreateButton
							siteId={siteId}
							buttontext={messages.createAgentRefreshButton}
							touchpointSource={'browse-agents-fullscreen-header'}
							analyticsPayload={{
								actionSubjectId: 'browseAgentsCreateAgentButtonRefresh',
								attributes: {
									interactionSource: 'browse-agents-modal-header',
								},
							}}
							appearance="primary"
							iconBefore={AddIcon}
							onClickCreateAgent={onClickCreateAgent}
						/>
					)}
				</Flex>
			</Inline>
		</Flex>
	);
};
